import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Drawer } from '@/ui/Drawer';
import { Field } from '@/ui/Field';
import { descargarPlantilla, useImportar, type Recurso } from './api';

/**
 * Importar — cargar registros desde un Excel con las columnas del recurso.
 *
 * El detalle de los errores se pinta como texto: el vanilla lo insertaba con
 * `innerHTML` sin escapar, y ese texto trae el valor de la celda que falló, que
 * lo escribió quien llenó el archivo.
 */
export function Importar({ recurso, onCerrar }: { recurso: Recurso; onCerrar: () => void }) {
  const importar = useImportar();
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState('');
  const resultado = importar.data;

  return (
    <Drawer
      abierto
      onCambio={(v) => { if (!v && !importar.isPending) onCerrar(); }}
      titulo={`Importar a ${recurso.name}`}
      descripcion="Las columnas se emparejan por su etiqueta; las que no coincidan se ignoran."
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
          <Button
            loading={importar.isPending}
            disabled={!archivo}
            razonDeshabilitado="Elige primero el archivo"
            onClick={() => {
              if (!archivo) return;
              setError('');
              importar.mutate({ recursoId: recurso.id, archivo }, {
                onSuccess: (r) => Utils.toast(
                  `${r.created} ${r.created === 1 ? 'registro importado' : 'registros importados'}`,
                  'success'),
                onError: (err) => setError(
                  err instanceof Error ? err.message : 'No se pudo importar'),
              });
            }}
          >
            <i className="bi bi-upload" aria-hidden="true" /> Importar
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <ol className="flex flex-col gap-1 rounded-sm border border-line bg-sunken p-3 text-2 text-ink-2">
          <li><strong>1.</strong> Descarga la plantilla: trae tus columnas.</li>
          <li><strong>2.</strong> Llénala en Excel.</li>
          <li><strong>3.</strong> Súbela aquí.</li>
        </ol>

        <div>
          <Button variant="secondary" size="sm" onClick={() => void descargarPlantilla(recurso.id)}>
            <i className="bi bi-download" aria-hidden="true" /> Descargar plantilla
          </Button>
        </div>

        <Field label="Archivo de Excel (.xlsx)" error={error || undefined}>
          <input
            type="file"
            accept=".xlsx,.xls"
            className="block w-full text-3 text-ink-2 file:mr-3 file:rounded-sm file:border-0 file:bg-brand file:px-3 file:py-2 file:text-3 file:font-semibold file:text-on-brand hover:file:bg-brand-strong"
            onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setError(''); }}
          />
        </Field>

        {resultado && (
          <section
            className="rounded-sm border border-ok/40 bg-ok/12 p-3 text-2 text-ink-2"
            aria-live="polite"
          >
            <p>
              <strong>{resultado.created}</strong>{' '}
              {resultado.created === 1 ? 'registro importado' : 'registros importados'}
              {resultado.errors ? <> · <strong>{resultado.errors}</strong> con error</> : null}
            </p>
            <p className="mt-1 text-muted">
              Columnas emparejadas: {resultado.mapped_columns.join(', ') || '—'}
            </p>
            {!!resultado.detail?.length && (
              <ul className="mt-2 max-h-40 overflow-auto text-1 text-muted">
                {resultado.detail.map((d, i) => (
                  <li key={i}>Fila {d.fila}: {d.error}</li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </Drawer>
  );
}
